import { supabase } from '../lib/supabase';
import { Product } from '../types';
import { productCache } from './productCache';
import { ProductUpdateService } from './productUpdateService';

export interface ProductDetail extends Product {
  // Additional fields for detailed view
  images: string[];
  description: string;
  categories?: string[];
  is_private_singlebuy: boolean;
  is_negotiable?: boolean;
  productType?: 'd' | 'a' | 'p';
  specifications: {
    brand: string;
    category: string;
    subcategory: string;
    material: string;
    condition: string;
    weight?: string;
    dimensions?: string;
  };
  seller: {
    id: string;
    name: string;
    avatar?: string;
    rating: number;
    reviewCount: number;
    verified: boolean;
    joinDate: string;
    location?: string;
  };
  reviews: {
    id: string;
    userId: string;
    userName: string;
    userAvatar?: string;
    rating: number;
    title: string;
    comment: string;
    images?: string[];
    helpful: number;
    createdAt: string;
    verified: boolean;
  }[];
  stats: {
    viewCount: number;
    favoriteCount: number;
    soldCount: number;
    stock: number;
  };
  tags: string[];
  createdAt: string;
  updatedAt: string;
  // Additional fields for editing
  customColors?: string[];
  sizeSystem?: string;
  model?: string;
  sku?: string;
  weight?: number;
  dimensions?: {
    length: number;
    width: number;
    height: number;
  };
  delivery_option_ids?: string[];
  // Required Product properties
  size: string[];
  color: string[];
  // Job-specific fields (only used when isJob = true)
  isJob: boolean;
  salaryType?: 'hourly' | 'fixed' | 'negotiable';
  location?: string;
  jobType?: 'full-time' | 'part-time' | 'contract' | 'freelance' | 'internship' | 'temporary' | 'volunteer';
  experienceLevel?: 'entry-level' | 'mid-level' | 'senior-level' | 'executive';
  duration?: string;
  remoteWorkAllowed?: boolean;
  requiredSkills?: string[];
  jobRequirements?: string[];
  benefitsPerks?: string[];
  contactEmail?: string;
  applicationDeadline?: string;
  expectedStartDate?: string;
  company?: {
    id: string;
    name: string;
    slug?: string;
    logoUrl?: string;
    industry?: string;
    headquartersCity?: string;
    headquartersState?: string;
    headquartersCountry?: string;
    description?: string;
    tagline?: string;
    foundedYear?: number;
    employeeCount?: number;
    isRemoteFriendly?: boolean;
    values?: string[];
    benefits?: string[];
    perks?: string[];
    website?: string;
    linkedinUrl?: string;
    isVerified?: boolean;
    isFeatured?: boolean;
  };
}

export class ProductDetailService {
  /**
   * Fetch detailed product information by ID with caching
   */
  static async getProductById(productId: string): Promise<ProductDetail | null> {
    try {
      // First, check if we have cached data
      const cachedProduct = productCache.get(productId);
      if (cachedProduct) {
        console.log('ProductDetailService - Using cached data for product:', productId);
        // If cache has legacy 'Unknown' category, try to refresh once
        if ((cachedProduct as any).category === 'Unknown') {
          try {
            const fresh = await this.fetchProductById(productId);
            if (fresh) {
              productCache.set(productId, fresh);
              return fresh;
            }
          } catch {}
        }
        return cachedProduct;
      }

      // If no cache, check if product exists and get updated_at
      const updateInfo = await ProductUpdateService.checkProductUpdate(productId);
      if (!updateInfo) {
        console.log('ProductDetailService - Product not found or inaccessible:', productId);
        return null;
      }

      // Do not block inactive products; allow viewing detail page for inactive/sold

      // Fetch full product data
      console.log('ProductDetailService - Fetching fresh data for product:', productId);
      const product = await this.fetchProductById(productId);
      
      if (product) {
        // Cache the product data
        productCache.set(productId, product);
      }

      return product;
    } catch (error) {
      console.error('Error in getProductById:', error);
      return null;
    }
  }

  /**
   * Fetch detailed product information by ID (internal method)
   */
  private static async fetchProductById(productId: string): Promise<ProductDetail | null> {
    try {
       const { data, error } = await supabase
         .from('products')
         .select(`*`)
         .eq('id', productId)
         .single();

      if (error) {
        console.error('Error fetching product:', error);
        console.error('Error details:', error.message, error.code, error.details);
        // If product not found due to RLS, try without is_active filter
        if (error.code === 'PGRST116') {
          const { data: fallbackData, error: fallbackError } = await supabase
            .from('products')
            .select(`*`)
            .eq('id', productId)
            .single();

          if (fallbackError) {
            console.error('Error fetching product (fallback):', fallbackError);
            console.error('Fallback error details:', fallbackError.message, fallbackError.code, fallbackError.details);
            return null;
          }

          if (!fallbackData) {
            return null;
          }

          // Type guard to ensure fallbackData is not an error
          if (typeof fallbackData === 'object' && 'code' in fallbackData) {
            console.error('Fallback data is an error object:', fallbackData);
            return null;
          }

          return this.transformProductData(fallbackData);
        }
        return null;
      }

      if (!data) {
        return null;
      }

      // Type guard to ensure data is not an error
      if (typeof data === 'object' && 'code' in data) {
        console.error('Data is an error object:', data);
        return null;
      }

      console.log('ProductDetailService.fetchProductById - Raw data:', data);
      console.log('Product is_job:', (data as any).is_job);
      console.log('Product company_id:', (data as any).company_id);
      console.log('Product auction_id:', (data as any).auction_id);
      console.log('Product auctions:', (data as any).auctions);
      console.log('ProductDetailService.fetchProductById - Images:', (data as any).images);
      console.log('ProductDetailService.fetchProductById - Seller ID:', (data as any).seller_id);

      return this.transformProductData(data);
    } catch (error) {
      console.error('Error in fetchProductById:', error);
      return null;
    }
  }

  /**
   * Transform raw product data to ProductDetail interface
   */
  private static async transformProductData(data: any): Promise<ProductDetail> {
    // Ensure data is not an error object
    if (typeof data === 'object' && data !== null && 'code' in data) {
      throw new Error('Data is an error object, cannot transform');
    }
    // Fetch seller information separately
    let sellerInfo = null;
    if (data.seller_id) {
      try {
        const { data: sellerData, error: sellerError } = await supabase
          .from('users')
          .select('alias, created_at')
          .eq('id', data.seller_id)
          .single();
        
        if (!sellerError && sellerData) {
          sellerInfo = sellerData;
        }
      } catch (sellerErr) {
        console.log('Could not fetch seller info:', sellerErr);
      }
    }

    console.log('ProductDetailService.getProductById - Seller Info:', sellerInfo);

    // Transform the data to match our ProductDetail interface
    // Prefer new categories[] array over legacy joins
    const primaryPath: string | undefined = Array.isArray(data.categories) && typeof data.categories[0] === 'string'
      ? data.categories[0]
      : undefined;
    const segs: string[] = typeof primaryPath === 'string' 
      ? primaryPath.split(' / ').map((s: string) => s.trim()).filter(Boolean)
      : [];
    const mainCategory: string = segs[0] || (data.categories?.name ?? 'Unknown');
    const subCategory: string = segs[1] || (data.subcategories?.name ?? '');

    const productDetail: ProductDetail = {
      id: data.id,
      title: data.name,
      description: data.description || '',
      price: data.price,
      originalPrice: data.original_price,
      images: data.images || [],
      categories: Array.isArray(data.categories) ? data.categories : [],
      category: mainCategory || 'Unknown',
      subcategory: subCategory || '',
      brand: data.brand || 'Unknown',
      condition: data.condition || 'new',
      material: data.material || 'Not specified',
      size: data.sizes || [],
      color: [...(data.colors || []), ...(data.custom_colors || [])],
      stock: data.is_sold ? 0 : 1, // Use is_sold to determine stock
      rating: 0, // Default rating since it doesn't exist in schema
      reviewCount: 0, // Default review count since it doesn't exist in schema
      sellerId: data.seller_id,
      productType: (data as any).product_type,
      is_negotiable: (data as any).is_negotiable === true,
      // Additional fields for editing
      customColors: data.custom_colors || [],
      sizeSystem: data.size_system || '',
      model: data.model || '',
      sku: data.sku || '',
      weight: data.weight || undefined,
      dimensions: data.dimensions || undefined,
      seller: {
        id: data.seller_id || '',
        name: sellerInfo?.alias || 'Unknown Seller',
        avatar: undefined,
        rating: 0,
        reviewCount: 0,
        verified: false,
        joinDate: sellerInfo?.created_at || '',
        location: undefined,
      },
      reviews: [], // Empty reviews array since reviews table doesn't exist yet
      stats: {
        viewCount: data.view_count || 0,
        favoriteCount: data.favorite_count || 0,
        soldCount: data.is_sold ? 1 : 0,
        stock: data.is_sold ? 0 : 1,
      },
      tags: data.tags || [],
      delivery_option_ids: data.delivery_option_ids || [],
      specifications: {
        brand: data.brand || 'Unknown',
        category: mainCategory || 'Unknown',
        subcategory: subCategory || '',
        material: data.material || 'Not specified',
        condition: data.condition || 'new',
        weight: data.weight ? `${data.weight} kg` : undefined,
        dimensions: data.dimensions ? 
          (typeof data.dimensions === 'string' ? data.dimensions : 
           `${data.dimensions.length || 0} × ${data.dimensions.width || 0} × ${data.dimensions.height || 0} cm`) : 
          undefined,
      },
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      isActive: data.is_active,
      is_private_singlebuy: (data.is_private_singlebuy ?? false) === true,
      isJob: data.is_job || false,
      // Job-specific fields
      salaryType: data.salary_type,
      location: data.location,
      jobType: data.job_type,
      experienceLevel: data.experience_level,
      duration: data.duration,
      remoteWorkAllowed: data.remote_work_allowed,
      requiredSkills: data.required_skills || [],
      jobRequirements: data.job_requirements || [],
      benefitsPerks: data.benefits_perks || [],
      contactEmail: data.contact_email,
      applicationDeadline: data.application_deadline,
      expectedStartDate: data.expected_start_date,
      // Auction fields - will be fetched separately if needed
      auctionId: data.auction_id,
      auction: undefined, // We'll fetch auction data separately to avoid join issues
    };

    // If this is a job, fetch company information
    if (data.is_job && data.company_id) {
      console.log('Fetching company data for company_id:', data.company_id);
      try {
        // First try to get company data with basic fields that definitely exist
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select(`
            id, 
            name, 
            slug,
            logo_url, 
            industry, 
            headquarters_city,
            headquarters_state,
            headquarters_country,
            description,
            tagline,
            founded_year,
            employee_count,
            is_remote_friendly,
            values,
            benefits,
            perks,
            website,
            linkedin_url,
            is_verified,
            is_hiring,
            is_active
          `)
          .eq('id', data.company_id)
          .single();
        
        console.log('Company data result:', companyData);
        console.log('Company error:', companyError);
        
        if (!companyError && companyData) {
          productDetail.company = {
            id: companyData.id,
            name: companyData.name,
            slug: companyData.slug,
            logoUrl: companyData.logo_url,
            industry: companyData.industry,
            headquartersCity: companyData.headquarters_city,
            headquartersState: companyData.headquarters_state,
            headquartersCountry: companyData.headquarters_country,
            description: companyData.description,
            tagline: companyData.tagline,
            foundedYear: companyData.founded_year,
            employeeCount: companyData.employee_count,
            isRemoteFriendly: companyData.is_remote_friendly || false,
            values: companyData.values || [],
            benefits: companyData.benefits || [],
            perks: companyData.perks || [],
            website: companyData.website,
            linkedinUrl: companyData.linkedin_url,
            isVerified: companyData.is_verified || false,
            isFeatured: companyData.is_hiring || false, // Use is_hiring as featured indicator
          };
          console.log('Company data assigned to productDetail:', productDetail.company);
        } else {
          console.log('No company data found or error occurred:', companyError);
          // Try a simpler query as fallback
          const { data: simpleCompanyData, error: simpleError } = await supabase
            .from('companies')
            .select('id, name, description, industry')
            .eq('id', data.company_id)
            .single();
          
          if (!simpleError && simpleCompanyData) {
            productDetail.company = {
              id: simpleCompanyData.id,
              name: simpleCompanyData.name,
              description: simpleCompanyData.description,
              industry: simpleCompanyData.industry,
            };
            console.log('Fallback company data assigned:', productDetail.company);
          }
        }
      } catch (companyErr) {
        console.log('Could not fetch company info:', companyErr);
      }
    } else {
      console.log('Not fetching company data - is_job:', data.is_job, 'company_id:', data.company_id);
    }

    return productDetail;
  }

  /**
   * Fetch auction data for a product
   */
  static async getAuctionData(productId: string): Promise<any> {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('auction_id')
        .eq('id', productId)
        .single();

      if (error || !data?.auction_id) {
        return null;
      }

      const { data: auctionData, error: auctionError } = await supabase
        .from('auctions')
        .select('*')
        .eq('id', data.auction_id)
        .single();

      if (auctionError) {
        console.error('Error fetching auction data:', auctionError);
        return null;
      }

      return auctionData;
    } catch (error) {
      console.error('Error in getAuctionData:', error);
      return null;
    }
  }

  /**
   * Fetch product by ID for editing (includes inactive products) with caching
   */
  static async getProductByIdForEdit(productId: string): Promise<ProductDetail | null> {
    try {
      // First, check if we have cached data
      const cachedProduct = productCache.get(productId);
      if (cachedProduct) {
        console.log('ProductDetailService - Using cached data for edit:', productId);
        return cachedProduct;
      }

      // If no cache, check if product exists and get updated_at
      const updateInfo = await ProductUpdateService.checkProductUpdate(productId);
      if (!updateInfo) {
        console.log('ProductDetailService - Product not found for edit:', productId);
        return null;
      }

      // Fetch full product data (including inactive products for editing)
      console.log('ProductDetailService - Fetching fresh data for edit:', productId);
      const product = await this.fetchProductByIdForEdit(productId);
      
      if (product) {
        // Cache the product data
        productCache.set(productId, product);
      }

      return product;
    } catch (error) {
      console.error('Error in getProductByIdForEdit:', error);
      return null;
    }
  }

  /**
   * Fetch product by ID for editing (internal method)
   */
  private static async fetchProductByIdForEdit(productId: string): Promise<ProductDetail | null> {
    try {
       const { data, error } = await supabase
         .from('products')
         .select(`*`)
         .eq('id', productId)
         .single(); // Removed .eq('is_active', true) for editing

      if (error) {
        console.error('Error fetching product for edit:', error);
        return null;
      }

      console.log('Raw product data from database:', data);
      console.log('Product is_job:', data.is_job);
      console.log('Product company_id:', data.company_id);

      if (!data) {
        return null;
      }

      return this.transformProductData(data);
    } catch (error) {
      console.error('Error in fetchProductByIdForEdit:', error);
      return null;
    }
  }

  /**
   * Invalidate cache for a product (call this after updating a product)
   */
  static invalidateCache(productId: string): void {
    productCache.invalidate(productId);
    console.log('ProductDetailService - Cache invalidated for product:', productId);
  }

  /**
   * Clear all cached products
   */
  static clearCache(): void {
    productCache.clear();
    console.log('ProductDetailService - All cache cleared');
  }

  /**
   * Get cache statistics
   */
  static getCacheStats(): { size: number; keys: string[] } {
    return productCache.getStats();
  }

  /**
   * Check if product is cached
   */
  static isProductCached(productId: string): boolean {
    return productCache.get(productId) !== null;
  }

  /**
   * Increment product view count
   */
  static async incrementViewCount(productId: string): Promise<void> {
    try {
      await supabase.rpc('increment_product_view_count', {
        product_uuid: productId,
      });
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  }

  /**
   * Add product to favorites
   */
  static async addToFavorites(productId: string, userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('product_favorites')
        .insert({
          product_id: productId,
          user_id: userId,
        });

      if (error) {
        console.error('Error adding to favorites:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in addToFavorites:', error);
      return false;
    }
  }

  /**
   * Remove product from favorites
   */
  static async removeFromFavorites(productId: string, userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('product_favorites')
        .delete()
        .eq('product_id', productId)
        .eq('user_id', userId);

      if (error) {
        console.error('Error removing from favorites:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in removeFromFavorites:', error);
      return false;
    }
  }

  /**
   * Check if product is favorited by user
   */
  static async isFavorited(productId: string, userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('product_favorites')
        .select('id')
        .eq('product_id', productId)
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
        console.error('Error checking favorite status:', error);
        return false;
      }

      return !!data;
    } catch (error) {
      console.error('Error in isFavorited:', error);
      return false;
    }
  }

  /**
   * Get related products
   */
  static async getRelatedProducts(productId: string, categoryId?: string, limit: number = 4): Promise<Product[]> {
    try {
      let query = supabase
        .from('products')
        .select(`
          id,
          name,
          price,
          original_price,
          brand,
          rating,
          review_count,
          product_images!inner(image_url)
        `)
        .eq('is_active', true)
        .neq('id', productId);

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      const { data, error } = await query.limit(limit);

      if (error) {
        console.error('Error fetching related products:', error);
        return [];
      }

      return data?.map((product: any) => ({
        id: product.id,
        title: product.name,
        description: product.description || '',
        price: product.price,
        originalPrice: product.original_price,
        images: [product.product_images?.[0]?.image_url || '/placeholder-product.jpg'],
        category: '', // Will be filled by category service if needed
        subcategory: '',
        brand: product.brand || 'Unknown',
        condition: 'new',
        material: 'Not specified',
        size: product.sizes || [],
        color: [...(product.colors || []), ...(product.custom_colors || [])],
        stock: 0,
        rating: product.rating || 0,
        reviewCount: product.review_count || 0,
        sellerId: '',
        seller: {
          id: '',
          name: 'Unknown Seller',
          rating: 0,
          reviewCount: 0,
          verified: false,
          joinDate: '',
        },
        tags: [],
        createdAt: '',
        updatedAt: '',
        isActive: true,
        isJob: product.is_job || false,
      })) || [];
    } catch (error) {
      console.error('Error in getRelatedProducts:', error);
      return [];
    }
  }
}
